import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Container, Row, Col } from 'react-bootstrap';
import { Input, Fieldset, BackLink, Card, Details, Button, ErrorSummary } from 'nhsuk-react-components';
import { listImplantCentres, createImplantCentre, updateImplantCentre, deleteImplantCentre, getImplantCentreDetails } from '../../actions/implantCentreActions';
import { IMPLANT_CENTRE_CREATE_RESET, IMPLANT_CENTRE_UPDATE_RESET } from '../../constants/implantCentreConstants';
import Pagination from '../../components/pagination/Pagination.component';
import AppSpinner from '../../components/spinner/AppSpinner.component';
import Callout from '../../components/callout/Callout.component';
import Modal from '../../components/modal/Modal.component';
import PageWrapper from '../../components/layout/PageWrapper.component';

const emptyCentre = {
    name: '',
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    email: '',
    phoneNumber: '',
};

export default function ImplantCentreCRUDScreen({ history }) {
    const dispatch = useDispatch();

    useEffect(() => {
        document.title = "Edit Implant Centres";
    }, []);

    useEffect(() => {
        dispatch({ type: IMPLANT_CENTRE_CREATE_RESET });
        dispatch({ type: IMPLANT_CENTRE_UPDATE_RESET });
    }, [dispatch]);

    const [implantCentreListFilter, setimplantCentreListFilter] = useState({ query: '', page: 1, filter: 'Name' });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [newImplantCentre, setNewImplantCentre] = useState({ ...emptyCentre });
    const [updatedImplantCentre, setUpdatedImplantCentre] = useState({});

    const implantCentreList = useSelector((state) => state.implantCentreList);
    const { loading, error, implantCentres, currentPage, totalPages, totalCount } = implantCentreList;

    const implantCentreCreate = useSelector((state) => state.implantCentreCreate);
    const { success: successCreate, error: implantCentreCreateErrors, loading: implantCentreCreateLoading } = implantCentreCreate;

    const implantCentreUpdate = useSelector((state) => state.implantCentreUpdate);
    const { success: successUpdate, loading: implantCentreUpdateProcessing } = implantCentreUpdate;

    const implantCentreDetails = useSelector((state) => state.implantCentreDetails);
    const { implantCentre: implantCentreDet, loading: implantCentreDetailsLoading } = implantCentreDetails;

    useEffect(() => {
        dispatch(listImplantCentres(implantCentreListFilter));
    }, [dispatch, implantCentreListFilter]);

    useEffect(() => {
        if (implantCentreDet) {
            setUpdatedImplantCentre({ ...implantCentreDet });
        }
    }, [implantCentreDet]);

    useEffect(() => {
        if (successCreate) {
            setShowCreateModal(false);
            setNewImplantCentre({ ...emptyCentre });
            dispatch(listImplantCentres(implantCentreListFilter));
            dispatch({ type: IMPLANT_CENTRE_CREATE_RESET });
        }
    }, [successCreate, dispatch, implantCentreListFilter]);

    const handlePageChange = (newPage) => {
        setimplantCentreListFilter({ ...implantCentreListFilter, page: newPage });
    };

    const handleSearchChange = (e) => {
        setimplantCentreListFilter({ ...implantCentreListFilter, query: e.target.value, page: 1 });
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this implantCentre?')) {
            dispatch(deleteImplantCentre(id)).then(() => {
                dispatch(listImplantCentres(implantCentreListFilter));
                if (updatedImplantCentre?.id === id) {
                    setShowEditModal(false);
                    setUpdatedImplantCentre({});
                }
            });
        }
    };

    const handleEditImplantCentre = (id) => {
        dispatch(getImplantCentreDetails(id));
        setShowCreateModal(false);
        setShowEditModal(true);
    };

    const handleCloseEdit = () => {
        setShowEditModal(false);
        setUpdatedImplantCentre({});
        dispatch({ type: IMPLANT_CENTRE_UPDATE_RESET });
    };

    const handleCreateImplantCentre = () => {
        dispatch(createImplantCentre(newImplantCentre));
    };

    const handleUpdateImplantCentre = () => {
        dispatch(updateImplantCentre(updatedImplantCentre.id, updatedImplantCentre)).then(() => {
            dispatch(getImplantCentreDetails(updatedImplantCentre.id));
            dispatch(listImplantCentres(implantCentreListFilter));
        });
    };

    const openCreateModal = () => {
        setNewImplantCentre({ ...emptyCentre });
        dispatch({ type: IMPLANT_CENTRE_CREATE_RESET });
        setShowEditModal(false);
        setShowCreateModal(true);
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col md="12">
                            <div className="welcome-box">
                                <PageWrapper title="ImplantCentre Management" totalCount={totalCount} loading={loading} />
                                <BackLink
                                    asElement="a"
                                    href="#"
                                    onClick={() => history.goBack()}
                                >
                                    Return back
                                </BackLink>
                                <Button
                                    className="nhsuk-button--small nhsuk-u-margin-bottom-4"
                                    onClick={openCreateModal}
                                >
                                    + Create ImplantCentre
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    <div className="nhsuk-grid-column-one-half">
                        <Card>
                            <Card.Content>
                                <Card.Heading className="nhsuk-heading-m">
                                    <Input
                                        id="search-implantCentres"
                                        label="Search implantCentres"
                                        name="search-implantCentres"
                                        type="search"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                handleSearchChange(e);
                                            }
                                        }}
                                    />
                                </Card.Heading>
                                <Card.Description>
                                    <Fieldset>
                                        {implantCentres && implantCentres.length > 0 ? (
                                            implantCentres.map((implantCentre) => (
                                                <Details key={implantCentre.id || implantCentre._id}>
                                                    <Details.Summary>
                                                        {implantCentre.name}
                                                    </Details.Summary>
                                                    <Details.Text>
                                                        <p>
                                                            <strong>Address:</strong> {implantCentre.streetAddress}
                                                        </p>
                                                        <p>
                                                            <strong>City:</strong> {implantCentre.city}
                                                        </p>
                                                        <p>
                                                            <strong>State:</strong> {implantCentre.state}
                                                        </p>
                                                        <p>
                                                            <strong>Postal Code:</strong> {implantCentre.postalCode}
                                                        </p>
                                                        <p>
                                                            <strong>Country:</strong> {implantCentre.country}
                                                        </p>
                                                        <p>
                                                            <strong>Email:</strong> {implantCentre.email}
                                                        </p>
                                                        <p>
                                                            <strong>Phone Number:</strong> {implantCentre.phoneNumber}
                                                        </p>
                                                        <div className="compact-admin-table__actions">
                                                            <Button
                                                                className="nhsuk-button--small"
                                                                disabled={implantCentreDetailsLoading}
                                                                onClick={() => handleEditImplantCentre(implantCentre.id)}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                secondary
                                                                className="nhsuk-button--small btn-delete"
                                                                onClick={() => handleDelete(implantCentre.id)}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </Details.Text>
                                                </Details>
                                            ))
                                        ) : null}
                                    </Fieldset>
                                    {loading ? (
                                        <AppSpinner />
                                    ) : error ? (
                                        <p>{error}</p>
                                    ) : (
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            handlePageChange={handlePageChange}
                                        />
                                    )}
                                </Card.Description>
                            </Card.Content>
                        </Card>
                    </div>

                    <div className="nhsuk-grid-column-one-half" />
                </Container>
            </div>

            <Modal
                show={showCreateModal}
                handleClose={() => {
                    setShowCreateModal(false);
                    dispatch({ type: IMPLANT_CENTRE_CREATE_RESET });
                }}
                theme="light"
                maxWidth="560px"
            >
                <h2 className="nhsuk-heading-l">Create ImplantCentre</h2>
                {implantCentreCreateErrors && (
                    <ErrorSummary aria-labelledby="error-summary-title" role="alert" tabIndex="-1">
                        <ErrorSummary.Title id="error-summary-title">There is a problem</ErrorSummary.Title>
                        <ErrorSummary.Body>
                            <ul className="nhsuk-list nhsuk-list--bullet">
                                {implantCentreCreateErrors.map((error) => (
                                    <li key={error}>{error}</li>
                                ))}
                            </ul>
                        </ErrorSummary.Body>
                    </ErrorSummary>
                )}
                <Input
                    id="name-create"
                    label="Name"
                    name="name-create"
                    type="text"
                    onChange={(e) => setNewImplantCentre({ ...newImplantCentre, name: e.target.value })}
                    value={newImplantCentre?.name || ''}
                    required
                />
                <Input
                    id="streetAddress-create"
                    label="Street Address"
                    name="streetAddress-create"
                    type="text"
                    onChange={(e) => setNewImplantCentre({ ...newImplantCentre, streetAddress: e.target.value })}
                    value={newImplantCentre?.streetAddress || ''}
                    required
                />
                <Input
                    id="city-create"
                    label="City"
                    name="city-create"
                    type="text"
                    onChange={(e) => setNewImplantCentre({ ...newImplantCentre, city: e.target.value })}
                    value={newImplantCentre?.city || ''}
                    required
                />
                <Input
                    id="state-create"
                    label="State"
                    name="state-create"
                    type="text"
                    onChange={(e) => setNewImplantCentre({ ...newImplantCentre, state: e.target.value })}
                    value={newImplantCentre?.state || ''}
                    required
                />
                <Input
                    id="postalCode-create"
                    label="Postal Code"
                    name="postalCode-create"
                    type="text"
                    onChange={(e) => setNewImplantCentre({ ...newImplantCentre, postalCode: e.target.value })}
                    value={newImplantCentre?.postalCode || ''}
                    required
                />
                <Input
                    id="country-create"
                    label="Country"
                    name="country-create"
                    type="text"
                    onChange={(e) => setNewImplantCentre({ ...newImplantCentre, country: e.target.value })}
                    value={newImplantCentre?.country || ''}
                    required
                />
                <Input
                    id="email-create"
                    label="Email"
                    name="email-create"
                    type="text"
                    onChange={(e) => setNewImplantCentre({ ...newImplantCentre, email: e.target.value })}
                    value={newImplantCentre?.email || ''}
                    required
                />
                <Input
                    id="phoneNumber-create"
                    label="Phone Number"
                    name="phoneNumber-create"
                    type="text"
                    onChange={(e) => setNewImplantCentre({ ...newImplantCentre, phoneNumber: e.target.value })}
                    value={newImplantCentre?.phoneNumber || ''}
                    required
                />
                <div className="compact-admin-table__actions nhsuk-u-margin-top-4">
                    <Button
                        className="nhsuk-button--small"
                        disabled={implantCentreCreateLoading || implantCentreUpdateProcessing}
                        onClick={handleCreateImplantCentre}
                    >
                        Create ImplantCentre
                    </Button>
                    <Button
                        secondary
                        className="nhsuk-button--small"
                        onClick={() => {
                            setShowCreateModal(false);
                            dispatch({ type: IMPLANT_CENTRE_CREATE_RESET });
                        }}
                    >
                        Cancel
                    </Button>
                </div>
            </Modal>

            <Modal
                show={showEditModal}
                handleClose={handleCloseEdit}
                theme="light"
                maxWidth="560px"
            >
                <h2 className="nhsuk-heading-l">Edit ImplantCentre</h2>
                {implantCentreDetailsLoading ? (
                    <AppSpinner />
                ) : (
                    <>
                        <Input
                            id="name"
                            label="Name"
                            name="name"
                            type="text"
                            onChange={(e) => setUpdatedImplantCentre({ ...updatedImplantCentre, name: e.target.value })}
                            value={updatedImplantCentre.name ? updatedImplantCentre.name : ''}
                            required
                        />
                        <Input
                            id="streetAddress"
                            label="Street Address"
                            name="streetAddress"
                            type="text"
                            onChange={(e) => setUpdatedImplantCentre({ ...updatedImplantCentre, streetAddress: e.target.value })}
                            value={updatedImplantCentre.streetAddress ? updatedImplantCentre.streetAddress : ''}
                            required
                        />
                        <Input
                            id="city"
                            label="City"
                            name="city"
                            type="text"
                            onChange={(e) => setUpdatedImplantCentre({ ...updatedImplantCentre, city: e.target.value })}
                            value={updatedImplantCentre.city ? updatedImplantCentre.city : ''}
                            required
                        />
                        <Input
                            id="state"
                            label="State"
                            name="state"
                            type="text"
                            onChange={(e) => setUpdatedImplantCentre({ ...updatedImplantCentre, state: e.target.value })}
                            value={updatedImplantCentre.state ? updatedImplantCentre.state : ''}
                            required
                        />
                        <Input
                            id="postalCode"
                            label="Postal Code"
                            name="postalCode"
                            type="text"
                            onChange={(e) => setUpdatedImplantCentre({ ...updatedImplantCentre, postalCode: e.target.value })}
                            value={updatedImplantCentre.postalCode ? updatedImplantCentre.postalCode : ''}
                            required
                        />
                        <Input
                            id="country"
                            label="Country"
                            name="country"
                            type="text"
                            onChange={(e) => setUpdatedImplantCentre({ ...updatedImplantCentre, country: e.target.value })}
                            value={updatedImplantCentre.country ? updatedImplantCentre.country : ''}
                            required
                        />
                        <Input
                            id="email"
                            label="Email"
                            name="email"
                            type="text"
                            onChange={(e) => setUpdatedImplantCentre({ ...updatedImplantCentre, email: e.target.value })}
                            value={updatedImplantCentre.email ? updatedImplantCentre.email : ''}
                            required
                        />
                        <Input
                            id="phoneNumber"
                            label="Phone Number"
                            name="phoneNumber"
                            type="text"
                            onChange={(e) => setUpdatedImplantCentre({ ...updatedImplantCentre, phoneNumber: e.target.value })}
                            value={updatedImplantCentre.phoneNumber ? updatedImplantCentre.phoneNumber : ''}
                            required
                        />
                        {successUpdate && (
                            <Callout
                                label="Success"
                                header="Success!"
                                autoDismiss={true}
                                dismissTime={5000}
                                time={new Date().toLocaleTimeString()}
                                title="Success!"
                                message="The implantCentre was updated successfully."
                                mode="success"
                            />
                        )}
                        <div className="compact-admin-table__actions nhsuk-u-margin-top-4">
                            <Button
                                className="nhsuk-button--small"
                                disabled={implantCentreUpdateProcessing}
                                onClick={handleUpdateImplantCentre}
                            >
                                Save changes
                            </Button>
                            <Button
                                secondary
                                className="nhsuk-button--small"
                                onClick={handleCloseEdit}
                            >
                                Cancel
                            </Button>
                        </div>
                    </>
                )}
            </Modal>
        </React.Fragment>
    );
}
